/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { TESTIDS } from "@/constants/testIds";
import { Plus, Search, Trash2, ChevronRight, Eye } from "lucide-react";
import { useAuth } from "@/lib/auth";
import BulkActionBar from "@/components/BulkActionBar";
import BulkEditModal from "@/components/BulkEditModal";
import Modal from "@/components/Modal";
import ImportExportButtons from "@/components/ImportExportButtons";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import { useAppSettings } from "@/hooks/useAppSettings";

const COMPANY_BULK_FIELDS = [
  { name: "Account_Tier", label: "Account Tier", type: "select", options: ["Standard", "Enterprise"] },
  { name: "Status", label: "Status", type: "select", options: ["Active", "Inactive"] },
  { name: "Discount_Percent", label: "Discount %", type: "number" },
  { name: "Portal_Access_Enabled", label: "Portal Access Enabled", type: "checkbox" },
  { name: "POCity", label: "City", type: "text" },
  { name: "PORegion", label: "Region", type: "text" },
  { name: "POPostalCode", label: "Postal Code", type: "text" },
  { name: "Phone", label: "Phone", type: "text" },
];

const TIERS = ["Standard", "Enterprise"];
const STATUSES = ["Active", "Inactive"];

function tierBadge(t) { return t === "Enterprise" ? "es-badge es-badge-brand" : "es-badge es-badge-neutral"; }
function statusBadge(s) { return s === "Active" ? "es-badge es-badge-success" : "es-badge es-badge-error"; }

export default function CompaniesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = ["admin", "user", "admin_reseller"].includes(user.role);
  const canDelete = user.role === "admin";

  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [filterTier, setFilterTier] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/companies"); setCompanies(data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = companies.filter(c =>
    (!q || c.Company_Name.toLowerCase().includes(q.toLowerCase())) &&
    (!filterTier || c.Account_Tier === filterTier) &&
    (!filterStatus || c.Status === filterStatus)
  );

  const filteredIds = filtered.map((c) => c.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllFiltered = () => setSelected((s) => {
    const n = new Set(s);
    if (allFilteredSelected) filteredIds.forEach((id) => n.delete(id));
    else filteredIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const applyBulk = async (patch) => {
    const items = Array.from(selected).map((id) => ({ id, ...patch }));
    try {
      const { data } = await api.post("/bulk/companies", { items });
      toast.success(`Updated ${data.updated_count} compan${data.updated_count === 1 ? "y" : "ies"}.`);
      if (data.errors?.length) toast.error(`${data.errors.length} record(s) failed.`);
      clearSelection();
      setShowBulk(false);
      load();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
      throw new Error(msg);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this company?")) return;
    try { await api.delete(`/companies/${id}`); setCompanies(cs => cs.filter(c => c.id !== id)); toast.success("Company deleted."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const allColumns = [
    ...(canManage ? [{
      key: "_select", label: "Select", locked: true, width: "w-10",
      header: () => (
        <input
          type="checkbox"
          data-testid="companies-select-all"
          checked={allFilteredSelected}
          onChange={toggleAllFiltered}
        />
      ),
      cell: (c) => (
        <input
          type="checkbox"
          data-testid={`company-select-${c.id}`}
          checked={selected.has(c.id)}
          onChange={() => toggleOne(c.id)}
        />
      ),
      stopRowClick: true,
    }] : []),
    {
      key: "Company_Name", label: "Company",
      cell: (c) => (
        <>
          <div className="font-semibold flex items-center gap-2">
            {c.Company_Name}
            <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />
          </div>
          <div className="text-xs text-zinc-500 font-mono">{c.Email || "—"}</div>
        </>
      ),
    },
    { key: "Account_Tier", label: "Tier", cell: (c) => <span className={tierBadge(c.Account_Tier)}>{c.Account_Tier}</span> },
    { key: "Status", label: "Status", cell: (c) => <span className={statusBadge(c.Status)}>{c.Status}</span> },
    { key: "Region", label: "Region", cell: (c) => c.POCity ? `${c.POCity}, ${c.PORegion}` : "—" },
    { key: "Discount_Percent", label: "Discount %", align: "right", cell: (c) => <span className="font-mono">{c.Discount_Percent}%</span> },
    { key: "Allocation", label: "Allocation", align: "right", cell: (c) => <span className="font-mono">{c.Paver_Allocation_Balance?.toLocaleString() || 0}</span> },
    {
      key: "Portal", label: "Portal",
      cell: (c) => c.Portal_Access_Enabled
        ? <span className="es-badge es-badge-success">Enabled</span>
        : <span className="es-badge es-badge-neutral">Off</span>,
    },
    { key: "Phone", label: "Phone", defaultVisible: false, cell: (c) => <span className="font-mono text-xs">{c.Phone || "—"}</span> },
    { key: "POAddress", label: "Address", defaultVisible: false, cell: (c) => <span className="text-xs">{[c.POAddressLine1, c.POAddressLine2].filter(Boolean).join(" · ") || "—"}</span> },
    { key: "POPostalCode", label: "Postcode", defaultVisible: false, cell: (c) => <span className="font-mono text-xs">{c.POPostalCode || "—"}</span> },
    { key: "ABN", label: "ABN", defaultVisible: false, cell: (c) => <span className="font-mono text-xs">{c.ABN || "—"}</span> },
    ...(canDelete ? [{
      key: "_actions", label: "Actions", locked: true, align: "right",
      cell: (c) => (
        <div className="flex items-center justify-end gap-1">
          {user.role === "admin" && (
            <button
              data-testid={`company-view-portal-${c.id}`}
              onClick={() => navigate(`/portal?as_company=${c.id}`)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-violet-100 text-violet-700 ring-1 ring-violet-200 hover:bg-violet-200 hover:text-violet-900 transition-colors"
              title={`View portal as ${c.Company_Name}`}
              aria-label={`View portal as ${c.Company_Name}`}
            >
              <Eye className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onDelete(c.id)} className="text-zinc-400 hover:text-red-600 p-1" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
      stopRowClick: true,
    }] : []),
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("companies", allColumns);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Companies</h1>
          <p className="text-sm text-zinc-500 mt-1">Partner registry — {filtered.length} of {companies.length} records shown.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ColumnChooser
            tableLabel="companies"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
          {canManage && (
            <>
              <ImportExportButtons
                entity="companies"
                rows={filtered.map(({ id, _id, created_date, updated_date, ...rest }) => rest)}
                exportColumns={[
                  "Company_Name", "Account_Tier", "Status", "Discount_Percent",
                  "Email", "Phone", "POAddressLine1", "POAddressLine2",
                  "POCity", "PORegion", "POPostalCode", "ABN", "TaxNumber",
                  "Portal_Access_Enabled", "Paver_Allocation_Balance",
                ]}
                exportName={`edenstone-companies-${new Date().toISOString().slice(0, 10)}`}
                onImported={load}
              />
              <button data-testid={TESTIDS.newCompanyBtn} onClick={() => setShowNew(true)} className="es-btn-primary">
                <Plus className="w-4 h-4" /> New company
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="es-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search company name…"
            className="es-input pl-9"
          />
        </div>
        <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)} className="es-select">
          <option value="">All tiers</option>
          {TIERS.map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="es-select">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      <div className="es-card overflow-x-auto">
        <table className="es-table">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th key={c.key} className={[c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "", c.width || ""].join(" ")}>
                  {c.header ? c.header() : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">No companies match your filters.</td></tr>
            )}
            {filtered.map((c) => (
              <tr
                key={c.id}
                data-testid={TESTIDS.companyRow(c.id)}
                onClick={() => navigate(`/companies/${c.id}`)}
                className={["cursor-pointer", selected.has(c.id) ? "bg-brand-50" : ""].join(" ")}
              >
                {visibleColumns.map((col) => (
                  <td
                    key={col.key}
                    className={col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : ""}
                    onClick={col.stopRowClick ? (e) => e.stopPropagation() : undefined}
                  >
                    {col.cell(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewCompanyModal onClose={() => setShowNew(false)} onCreated={(c) => { setCompanies(cs => [c, ...cs]); setShowNew(false); }} />}

      {canManage && (
        <BulkActionBar
          count={selected.size}
          label={selected.size === 1 ? "company selected" : "companies selected"}
          onEdit={() => setShowBulk(true)}
          onClear={clearSelection}
        />
      )}
      {showBulk && (
        <BulkEditModal
          title="Bulk Edit Companies"
          fields={COMPANY_BULK_FIELDS}
          selectedCount={selected.size}
          onClose={() => setShowBulk(false)}
          onApply={applyBulk}
        />
      )}
    </div>
  );
}

export function NewCompanyModal({ onClose, onCreated }) {
  const settings = useAppSettings();
  const [form, setForm] = useState(() => ({
    Company_Name: "", Account_Tier: "Standard", Status: "Active",
    Email: "", Phone: "", POCity: "", PORegion: "", POPostalCode: "",
    Discount_Percent: settings?.Default_Discount_Percent ?? 0, Portal_Access_Enabled: false,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // If settings loaded after the initial mount, sync the default discount unless
  // the user has already touched it.
  const [discountTouched, setDiscountTouched] = useState(false);
  useEffect(() => {
    if (!discountTouched && settings?.Default_Discount_Percent != null) {
      setForm((f) => ({ ...f, Discount_Percent: settings.Default_Discount_Percent }));
    }
  }, [settings?.Default_Discount_Percent, discountTouched]);

  const update = (k, v) => {
    if (k === "Discount_Percent") setDiscountTouched(true);
    setForm(f => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.post("/companies", { ...form, Discount_Percent: Number(form.Discount_Percent) });
      onCreated(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="New Company" onClose={onClose}>
      <form onSubmit={onSubmit} className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="es-label">Company Name *</label>
            <input data-testid="new-company-Company_Name" required value={form.Company_Name} onChange={e => update("Company_Name", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Account Tier</label>
            <select data-testid="new-company-Account_Tier" value={form.Account_Tier} onChange={e => update("Account_Tier", e.target.value)} className="es-select w-full">
              {TIERS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="es-label">Status</label>
            <select data-testid="new-company-Status" value={form.Status} onChange={e => update("Status", e.target.value)} className="es-select w-full">
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="es-label">Email</label>
            <input data-testid="new-company-Email" type="email" value={form.Email} onChange={e => update("Email", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Phone</label>
            <input data-testid="new-company-Phone" value={form.Phone} onChange={e => update("Phone", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">City</label>
            <input data-testid="new-company-POCity" value={form.POCity} onChange={e => update("POCity", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Region</label>
            <input data-testid="new-company-PORegion" value={form.PORegion} onChange={e => update("PORegion", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Postal Code</label>
            <input data-testid="new-company-POPostalCode" value={form.POPostalCode} onChange={e => update("POPostalCode", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Discount %</label>
            <input data-testid="new-company-Discount_Percent" type="number" min="0" max="100" value={form.Discount_Percent} onChange={e => update("Discount_Percent", e.target.value)} className="es-input" />
          </div>
          <label className="sm:col-span-2 flex items-center gap-2 mt-2">
            <input data-testid="new-company-Portal_Access_Enabled" type="checkbox" checked={form.Portal_Access_Enabled} onChange={e => update("Portal_Access_Enabled", e.target.checked)} />
            <span className="text-sm">Enable reseller portal access</span>
          </label>

          {err && <div className="sm:col-span-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} data-testid={TESTIDS.newCompanySave} className="es-btn-primary">{saving ? "Saving…" : "Create company"}</button>
          </div>
      </form>
    </Modal>
  );
}
